import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Clock3, Heart, Loader2, MessageCircle, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";

export const QUESTION_CATEGORIES = [
  "Childhood & Family",
  "Daily Life",
  "Black Life & Identity",
  "Racism, Rights & Change",
  "Black Community & Culture",
  "Hair, Beauty & Style",
  "Faith & Spirituality",
  "Food & Family Traditions",
  "Music & Entertainment",
  "School & Education",
  "Migration & New Beginnings",
  "Love & Relationships",
  "Work & Community",
  "History & Change",
  "Edmonton & Canada",
  "Wisdom & Resilience",
  "Just for Fun",
];

const QUESTION_STARTERS = [
  {
    category: "Black Life & Identity",
    question: "Agnes, what made you feel proud to be a Black woman when you were young?",
  },
  {
    category: "Racism, Rights & Change",
    question: "What changes have you witnessed in how Black people are treated over your lifetime?",
  },
  {
    category: "Black Community & Culture",
    question: "Where did Black families gather, celebrate, and support one another when you were growing up?",
  },
  {
    category: "Hair, Beauty & Style",
    question: "How did Black women care for and style their hair when you were a girl?",
  },
  {
    category: "Faith & Spirituality",
    question: "What role did faith and the church play in your family and community?",
  },
  {
    category: "Food & Family Traditions",
    question: "Which family recipe or celebration tradition carries the most memories for you?",
  },
  {
    category: "School & Education",
    question: "What was school like for a young Black girl when you were growing up?",
  },
  {
    category: "Migration & New Beginnings",
    question: "What do you remember about the places your family called home and the journeys between them?",
  },
  {
    category: "Music & Entertainment",
    question: "Which songs, dances, or performers brought the most joy to your younger years?",
  },
  {
    category: "Wisdom & Resilience",
    question: "What helped you remain strong during the hardest times in your life?",
  },
];

const categoryTones = [
  "bg-terracotta/10 text-terracotta",
  "bg-gold/20 text-forest",
  "bg-forest/10 text-forest",
];

function formatDate(value) {
  if (!value) return "Recently";
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

function QuestionCard({ item, index }) {
  return (
    <article
      data-testid={`question-card-${item.id}`}
      className={`group rounded-[1.75rem] border border-border/80 bg-cream p-6 shadow-[0_14px_40px_-28px_rgba(44,53,37,.6)] transition duration-300 hover:-translate-y-1 hover:border-terracotta/40 hover:shadow-[0_22px_45px_-28px_rgba(142,76,54,.55)] ${index % 3 === 1 ? "md:translate-y-8" : ""}`}
    >
      <div className="flex items-center justify-between gap-4">
        <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${categoryTones[index % categoryTones.length]}`}>
          {item.category}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-xs text-forest/45">
          <Clock3 className="h-3.5 w-3.5" /> {formatDate(item.created_at)}
        </span>
      </div>
      <p className="mt-6 font-serif text-[1.35rem] leading-snug text-forest">“{item.question}”</p>
      <div className="mt-6 flex items-center gap-2 text-sm text-forest/55">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-terracotta/10 font-hand text-xl text-terracotta">
          {(item.asker_name || "A").slice(0, 1).toUpperCase()}
        </span>
        <span>{item.asker_name || "A curious visitor"}</span>
      </div>
    </article>
  );
}

export default function AskAgnesPage() {
  const [questions, setQuestions] = useState([]);
  const [category, setCategory] = useState("All questions");
  const [form, setForm] = useState({ asker_name: "", category: QUESTION_CATEGORIES[0], question: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadQuestions = () => {
    setLoading(true);
    setError("");
    api.get("/questions")
      .then((response) => setQuestions(response.data?.questions || []))
      .catch((err) => setError(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadQuestions(); }, []);

  const filteredQuestions = useMemo(
    () => category === "All questions" ? questions : questions.filter((item) => item.category === category),
    [category, questions],
  );

  const submitQuestion = async (event) => {
    event.preventDefault();
    if (!form.question.trim()) return toast.error("Write a question for Agnes first.");
    setSubmitting(true);
    try {
      await api.post("/questions", {
        asker_name: form.asker_name.trim() || null,
        category: form.category,
        question: form.question.trim(),
      });
      setForm({ asker_name: "", category: form.category, question: "" });
      toast.success("Your question has joined the conversation.");
      loadQuestions();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const useQuestionStarter = (starter) => {
    setForm({
      ...form,
      category: starter.category,
      question: starter.question,
    });
    document.getElementById("question-input")?.focus();
  };

  return (
    <Layout>
      <section className="relative overflow-hidden bg-forest px-5 pb-20 pt-16 text-cream md:pb-28 md:pt-24">
        <div className="absolute -right-24 -top-28 h-80 w-80 rounded-full border border-gold/20" />
        <div className="absolute -right-8 -top-12 h-48 w-48 rounded-full border border-gold/15" />
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-gold">
              <Sparkles className="h-4 w-4" /> 107 years old · A century of stories
            </p>
            <h1 className="mt-6 font-serif text-5xl font-semibold leading-[0.95] md:text-7xl">
              Ask Agnes<br /><em className="font-normal text-terracotta">anything.</em>
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-cream/70">
              At 107 years old, Agnes Murphy holds more than a century of memories. Ask what you have always wondered, and explore the questions other curious hearts have shared.
            </p>
            <a href="#ask-form" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-forest transition hover:-translate-y-0.5 hover:bg-gold/90" data-testid="link-ask-question">
              Leave a question <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="mt-16 grid max-w-2xl grid-cols-3 gap-4 border-t border-cream/15 pt-6 text-sm text-cream/60">
            <div><p className="font-serif text-3xl text-cream">{questions.length}</p><p>questions shared</p></div>
            <div><p className="font-serif text-3xl text-cream">107</p><p>years of perspective</p></div>
            <div><p className="font-serif text-3xl text-cream">∞</p><p>stories to uncover</p></div>
          </div>
        </div>
      </section>

      <section id="ask-form" className="mx-auto grid max-w-6xl gap-12 px-5 py-16 md:grid-cols-[.82fr_1.18fr] md:py-24">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Your turn</p>
          <h2 className="mt-4 font-serif text-4xl font-semibold leading-tight text-forest md:text-5xl">What have you always wondered?</h2>
            <p className="mt-5 leading-relaxed text-forest/65">Ask about everyday joys, Black life and identity, family traditions, injustice and change, or the wisdom gathered across 107 years. Names are optional.</p>
          <div className="mt-9 flex items-start gap-4 rounded-2xl border border-gold/30 bg-gold/10 p-5">
            <Heart className="mt-1 h-5 w-5 shrink-0 text-terracotta" />
            <p className="text-sm leading-relaxed text-forest/70">Questions are collected as part of Agnes’s birthday oral history. Keep them kind, curious, and true to you.</p>
          </div>
        </div>
        <form onSubmit={submitQuestion} className="rounded-[2rem] border border-border bg-cream p-6 shadow-[0_25px_70px_-40px_rgba(44,53,37,.7)] md:p-8">
          <label className="block text-sm font-semibold text-forest">Your question <span className="text-terracotta">*</span>
            <textarea id="question-input" data-testid="input-question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} maxLength={500} rows={5} placeholder="Agnes, what was life like for a young Black girl when you were growing up?" className="mt-2 w-full resize-none rounded-2xl border border-input bg-background px-4 py-3 font-serif text-lg text-forest outline-none transition focus:border-terracotta focus:ring-4 focus:ring-terracotta/10" />
          </label>
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-forest/45">Choose a question starter</p>
            <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
              {QUESTION_STARTERS.map((starter) => (
                <button
                  key={starter.question}
                  type="button"
                  onClick={() => useQuestionStarter(starter)}
                  className="max-w-[17rem] shrink-0 rounded-xl border border-gold/35 bg-gold/10 px-4 py-3 text-left text-xs leading-relaxed text-forest/75 transition hover:border-terracotta hover:bg-terracotta/5 hover:text-forest"
                >
                  {starter.question}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <label className="block text-sm font-semibold text-forest">Your name <span className="font-normal text-forest/40">(optional)</span>
              <input data-testid="input-asker-name" value={form.asker_name} onChange={(e) => setForm({ ...form, asker_name: e.target.value })} maxLength={80} placeholder="How should Agnes call you?" className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none transition focus:border-terracotta focus:ring-4 focus:ring-terracotta/10" />
            </label>
            <label className="block text-sm font-semibold text-forest">Choose a chapter of her story
              <select data-testid="select-question-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none transition focus:border-terracotta focus:ring-4 focus:ring-terracotta/10">
                {QUESTION_CATEGORIES.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
          </div>
          <button type="submit" disabled={submitting} data-testid="button-submit-question" className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-terracotta px-5 py-3.5 font-semibold text-cream transition hover:bg-terracotta-dark disabled:cursor-not-allowed disabled:opacity-60">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {submitting ? "Sending..." : "Send it to Agnes"}
          </button>
        </form>
      </section>

      <section className="bg-muted/45 px-5 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">The question room</p>
              <h2 className="mt-3 font-serif text-4xl font-semibold text-forest md:text-5xl">Curiosity, collected.</h2>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {["All questions", ...QUESTION_CATEGORIES].map((item) => (
                <button key={item} onClick={() => setCategory(item)} data-testid={`filter-category-${item}`} className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-medium transition ${category === item ? "border-forest bg-forest text-cream" : "border-border bg-background text-forest/65 hover:border-terracotta hover:text-terracotta"}`}>{item}</button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="mt-12 grid gap-5 md:grid-cols-3">{[1, 2, 3].map((item) => <div key={item} className="h-64 animate-pulse rounded-[1.75rem] bg-cream/70" />)}</div>
          ) : error ? (
            <div className="mt-12 rounded-2xl border border-terracotta/30 bg-cream p-8 text-center"><p className="text-forest/70">{error}</p><button onClick={loadQuestions} className="mt-4 text-sm font-semibold text-terracotta underline" data-testid="button-retry-questions">Try again</button></div>
          ) : filteredQuestions.length === 0 ? (
            <div className="mt-12 rounded-[2rem] border border-dashed border-border bg-cream px-6 py-16 text-center"><MessageCircle className="mx-auto h-8 w-8 text-terracotta" /><p className="mt-4 font-serif text-2xl text-forest">Be the first voice in this room.</p><p className="mt-2 text-sm text-forest/60">Your question could be the one that unlocks a wonderful story.</p></div>
          ) : (
            <div className="mt-12 grid gap-5 md:grid-cols-3">{filteredQuestions.map((item, index) => <QuestionCard item={item} index={index} key={item.id} />)}</div>
          )}
        </div>
      </section>
    </Layout>
  );
}